import React from 'react';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useFormContext, FieldValues, Path, PathValue, useWatch } from 'react-hook-form';
import { Button } from '@mui/material';
import { useStacklineTheme } from '@stackline/ui';
import PathControlledDropdown from '../components/ui/PathControlledDropdown';
import {
  BeaconMetricsSchema,
  AtlasMetricsSchema,
  LogicalOperatorsSchema,
  ResponseTypesSchema
} from '../schema/AlertBuilderSchema';
import {
  SelectedProductType,
  RuleGroupConfigType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import RuleGroup from '../components/RuleGroup';
import { createDefaultRuleGroup, DEFAULT_LOGICAL_OPERATOR } from '../utils';
import { getAppName } from 'src/utils/app';
import useCurrentAndFutureWeekId from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useCurrentAndFutureWeekId';
import { AppName } from 'sl-api-connector';
import ControlledMultiSelect from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/ControlledMultiSelect';

type Props<T extends FieldValues> = {};

// Map technical response type values to user-friendly display names
const UI_RESPONSE_TYPE_DISPLAY_MAP: Record<string, string> = {
  CategoryId: 'Category',
  SubcategoryId: 'Subcategory',
  BrandId: 'Brand',
  Searchterm: 'Keyword',
  WeekId: 'Week Id',
  RetailerId: 'Retailer Id',
  RetailerSku: 'Retailer SKU'
};

// Get all possible response type options
const RESPONSE_TYPE_OPTIONS = ResponseTypesSchema.options.map((value) => ({
  value,
  label: UI_RESPONSE_TYPE_DISPLAY_MAP[value] || value,
  disabled: false
}));

// Convenience wrapper for Response Type selection
function ControlledResponseTypeSelect<T extends FieldValues>({ name }: { name: Path<T> }) {
  return (
    <ControlledMultiSelect
      name={name}
      title="Response Type"
      description="Select the response type of the alert. This determines how your data will be broken down in the alert. It is similar to a 'group by' in a typical Stackline export. This selection represents what each row in the report will have."
      options={RESPONSE_TYPE_OPTIONS}
    />
  );
}

function AlertConditionsTab<T extends FieldValues>({}: Props<T>) {
  const { setValue } = useFormContext<T>();
  const allWeekIds = useCurrentAndFutureWeekId();
  const theme = useStacklineTheme();

  // Type-safe path constants
  const RULE_GROUPS_PATH = 'queryJSON.rules.ruleGroups' as Path<T>;
  const GROUP_OPERATORS_PATH = 'queryJSON.rules.groupOperators' as Path<T>;

  // Helper functions to get specific paths
  const getGroupOperatorPath = (operatorIndex: number) => `${GROUP_OPERATORS_PATH}.${operatorIndex}` as Path<T>;

  const selectedProduct = getAppName() as SelectedProductType;
  const metricsOptions = selectedProduct === AppName.Atlas ? AtlasMetricsSchema.options : BeaconMetricsSchema.options;

  // Watch the form values for rules - these come from form context (default values or user input)
  const ruleGroups = useWatch({ name: RULE_GROUPS_PATH }) || ([] as RuleGroupConfigType[]);

  // Watch group operators from form context (gets default values from Zod paths)
  const groupOperatorsRaw = useWatch({ name: GROUP_OPERATORS_PATH });
  const groupOperators = React.useMemo(() => groupOperatorsRaw || [], [groupOperatorsRaw]);
  const masterOperatorType = groupOperators[0]?.type || DEFAULT_LOGICAL_OPERATOR;

  // Sync all group operators to match the first one when it changes
  React.useEffect(() => {
    if (groupOperators.length > 1) {
      const needsSync = groupOperators.slice(1).some((op) => op?.type !== masterOperatorType);
      if (needsSync) {
        const syncedOperators = groupOperators.map((op, index) => (index === 0 ? op : { type: masterOperatorType }));
        setValue(GROUP_OPERATORS_PATH, syncedOperators as PathValue<T, typeof GROUP_OPERATORS_PATH>);
      }
    }
  }, [masterOperatorType, groupOperators, setValue, GROUP_OPERATORS_PATH]);

  // Initialize with default rule group if empty
  React.useEffect(() => {
    if (ruleGroups.length === 0) {
      setValue(RULE_GROUPS_PATH, [createDefaultRuleGroup(allWeekIds)] as PathValue<T, typeof RULE_GROUPS_PATH>);
    }
  }, [ruleGroups.length, setValue, allWeekIds]);

  const addRuleGroup = () => {
    setValue(RULE_GROUPS_PATH, [...ruleGroups, createDefaultRuleGroup(allWeekIds)] as PathValue<
      T,
      typeof RULE_GROUPS_PATH
    >);

    // Add matching operator for new rule group
    if (ruleGroups.length > 0) {
      setValue(GROUP_OPERATORS_PATH, [...groupOperators, { type: masterOperatorType }] as PathValue<
        T,
        typeof GROUP_OPERATORS_PATH
      >);
    }
  };

  return (
    <Flex flexDirection="column" gap={32}>
      {/* Response Type Selection */}
      <ControlledResponseTypeSelect<T> name={'queryJSON.basicInfo.granularity' as Path<T>} />

      {/* Rules section */}
      <Flex flexDirection="column" gap={16}>
        <Text variant="h3" sx={{ fontWeight: 600 }}>
          Rules
        </Text>
        <Text variant="body2" sx={{ fontSize: '16px', fontWeight: 400, lineHeight: '24px' }}>
          Create rules to define when alerts should be triggered.
        </Text>

        {/* Rule Groups */}
        {ruleGroups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {/* Group Operator (between rule groups) */}
            {groupIndex > 0 && (
              <Flex justifyContent="center" style={{ margin: '16px 0' }}>
                <PathControlledDropdown<T>
                  name={`${getGroupOperatorPath(groupIndex - 1)}.type` as Path<T>}
                  label=""
                  options={groupIndex === 1 ? LogicalOperatorsSchema.options : [masterOperatorType]}
                  disabled={groupIndex > 1}
                  sx={{
                    minWidth: '120px',
                    '& .MuiInputBase-root': {
                      backgroundColor: groupIndex === 1 ? '#f0f8ff' : '#f5f5f5',
                      fontWeight: 500,
                      opacity: groupIndex > 1 ? 0.7 : 1
                    }
                  }}
                />
              </Flex>
            )}

            <RuleGroup<T> groupIndex={groupIndex} metricsOptions={metricsOptions} />
          </React.Fragment>
        ))}

        {/* Add Rule Group Button */}
        <Flex justifyContent="flex-start">
          <Button
            variant="text"
            onClick={addRuleGroup}
            sx={{
              color: theme.colors.primary,
              textTransform: 'none',
              fontSize: '14px',
              padding: '8px 0'
            }}
          >
            + Add Rule Group
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default AlertConditionsTab;
